import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, X, Clock, CircleCheck as CheckCircle, Package, Bike, Chrome as Home, User, History } from 'lucide-react';
import { ORDER_TRACKING_URL, ORDER_TIMELINE_URL } from '../../config/api';
import { authenticatedFetch } from '../Utils/apiHelpers';
import './OrderTrackingPanel.css';

interface TrackingStage {
  stage: string;
  completed: boolean;
}

interface Rider {
  name?: string;
  phone?: string;
  [key: string]: unknown;
}

interface TrackingData {
  success: boolean;
  order_id: number;
  status: string;
  eta_minutes: number;
  estimated_delivery: string;
  timeline: TrackingStage[];
  rider: Rider | null;
}

interface TimelineEntry {
  timeline_id: number;
  order_id: number;
  status: string;
  message: string;
  created_at: string;
}

interface TimelineData {
  success: boolean;
  order_id: number;
  timeline: TimelineEntry[];
}

interface OrderTrackingPanelProps {
  orderId: number;
  onClose: () => void;
}

const STAGE_META: Record<string, { label: string; Icon: React.FC<{ size?: number }> }> = {
  waiting_kitchen_confirmation: { label: 'Awaiting Kitchen Confirmation', Icon: Clock },
  confirmed:  { label: 'Order Confirmed', Icon: CheckCircle },
  preparing:  { label: 'Preparing',       Icon: Package },
  picked:     { label: 'Picked Up',       Icon: Bike },
  on_the_way: { label: 'On the Way',      Icon: Bike },
  delivered:  { label: 'Delivered',       Icon: Home },
};

const POLL_INTERVAL = 30000;

function formatETA(isoStr: string): string {
  try {
    return new Date(isoStr).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return isoStr;
  }
}

function formatTimestamp(isoStr: string): string {
  try {
    return new Date(isoStr).toLocaleString('en-GB', {
      day: 'numeric', month: 'short',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return isoStr;
  }
}

const OrderTrackingPanel: React.FC<OrderTrackingPanelProps> = ({ orderId, onClose }) => {
  const [data, setData] = useState<TrackingData | null>(null);
  const [timelineData, setTimelineData] = useState<TimelineEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showHistory, setShowHistory] = useState(false);

  const fetchTracking = useCallback(async () => {
    try {
      const [trackRes, timelineRes] = await Promise.all([
        authenticatedFetch(ORDER_TRACKING_URL(orderId), {
          headers: { 'Accept': 'application/json' },
        }),
        authenticatedFetch(ORDER_TIMELINE_URL(orderId), {
          headers: { 'Accept': 'application/json' },
        }),
      ]);

      if (trackRes.ok) {
        const json: TrackingData = await trackRes.json();
        setData(json);
        setError('');
      } else if (trackRes.status === 404) {
        setError('Tracking information is not available for this order yet.');
      } else {
        setError('Failed to load tracking information.');
      }

      if (timelineRes.ok) {
        const tj: TimelineData = await timelineRes.json();
        setTimelineData(tj.timeline ?? []);
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    fetchTracking();
    const interval = setInterval(fetchTracking, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchTracking]);

  const activeIndex = data
    ? data.timeline.findLastIndex(s => s.completed)
    : -1;

  return (
    <div className="otp-panel">
      {/* Panel header */}
      <div className="otp-header">
        <div className="otp-header-left">
          <span className="otp-title">Track Order #{orderId}</span>
          {data && (
            <span className="otp-live-dot" title="Live tracking" />
          )}
        </div>
        <div className="otp-header-right">
          {timelineData.length > 0 && (
            <button
              className="otp-icon-btn"
              onClick={() => setShowHistory(h => !h)}
              title="Status history"
            >
              <History size={14} />
            </button>
          )}
          <button
            className="otp-icon-btn"
            onClick={() => { setLoading(true); fetchTracking(); }}
            disabled={loading}
            title="Refresh"
          >
            <RefreshCw size={14} className={loading ? 'otp-spinning' : ''} />
          </button>
          <button className="otp-icon-btn" onClick={onClose} title="Close tracking">
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Body */}
      {loading && !data && (
        <div className="otp-state">
          <RefreshCw size={24} className="otp-spinning" />
          <span>Loading tracking info...</span>
        </div>
      )}

      {error && !data && (
        <div className="otp-state otp-state--error">
          <Clock size={20} />
          <span>{error}</span>
        </div>
      )}

      {data && (
        <div className="otp-body">
          {/* ETA strip */}
          <div className="otp-eta-strip">
            <div className="otp-eta-main">
              <span className="otp-eta-mins">{data.eta_minutes} min</span>
              <span className="otp-eta-label">estimated arrival</span>
            </div>
            <div className="otp-eta-time">
              <Clock size={14} />
              <span>{formatETA(data.estimated_delivery)}</span>
            </div>
          </div>

          {/* Tracking stages */}
          <ol className="otp-timeline">
            {data.timeline.map((stage, i) => {
              const meta = STAGE_META[stage.stage] ?? { label: stage.stage, Icon: Clock as React.FC<{ size?: number }> };
              const isActive = i === activeIndex && !data.timeline[data.timeline.length - 1].completed;
              const isDone = stage.completed;
              const stageClass = isDone ? 'otp-stage--done' : isActive ? 'otp-stage--active' : 'otp-stage--pending';
              const isLast = i === data.timeline.length - 1;

              return (
                <li key={stage.stage} className={`otp-stage ${stageClass}`}>
                  <div className="otp-stage-track">
                    <div className="otp-stage-dot">
                      {isDone ? (
                        <CheckCircle size={16} />
                      ) : isActive ? (
                        <meta.Icon size={14} />
                      ) : (
                        <span className="otp-dot-inner" />
                      )}
                    </div>
                    {!isLast && <div className={`otp-connector${isDone ? ' otp-connector--done' : ''}`} />}
                  </div>
                  <div className="otp-stage-info">
                    <span className="otp-stage-label">{meta.label}</span>
                    {isActive && <span className="otp-stage-tag">In progress</span>}
                  </div>
                </li>
              );
            })}
          </ol>

          {/* Timestamped status history */}
          {showHistory && timelineData.length > 0 && (
            <div className="otp-history">
              <span className="otp-history-title">Status History</span>
              <ul className="otp-history-list">
                {timelineData.map(entry => (
                  <li key={entry.timeline_id} className="otp-history-entry">
                    <span className="otp-history-msg">{entry.message}</span>
                    <span className="otp-history-time">{formatTimestamp(entry.created_at)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Rider card */}
          {data.rider && (
            <div className="otp-rider-card">
              <div className="otp-rider-icon">
                <User size={18} />
              </div>
              <div className="otp-rider-info">
                <span className="otp-rider-name">{data.rider.name ?? 'Rider assigned'}</span>
                {data.rider.phone && (
                  <a
                    className="otp-rider-phone"
                    href={`tel:${data.rider.phone}`}
                  >
                    {String(data.rider.phone)}
                  </a>
                )}
              </div>
              <span className="otp-rider-badge">Your Rider</span>
            </div>
          )}

          {error && (
            <p className="otp-inline-error">{error}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default OrderTrackingPanel;
